import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { docxBlobToPlateValue } from '../PlateEditor/serializers/docxBlobToPlateValue';
// Import directly from the serializer file, NOT the `../PlateEditor` barrel —
// the barrel also re-exports the full interactive PlateEditor (toolbar, AI
// menu), and a CJS require() of the barrel module pulls in that entire chain
// regardless of which named export is used.
import { plateToHtml } from '../PlateEditor/serializers/plateToHtml';
import { sanitizeUntrustedHtml } from '../../utils/sanitizeHtml';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import styles from './DocumentViewer.module.scss';
import plateStyles from '../PlateEditor/PlateEditor.module.scss';
import { OLE2_MAGIC } from './spreadsheetBytes';

// `.docx` is a ZIP/OOXML archive (PK\x03\x04 magic bytes); legacy `.doc` is an
// OLE2 compound file (D0 CF 11 E0 A1 B1 1A E1) — mammoth can only read the
// former, so content-based detection tells the two "not previewable" cases
// apart without needing a filename.
const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04];

const hasMagic = (buffer: ArrayBuffer, magic: number[]): boolean => {
    if (buffer.byteLength < magic.length) return false;
    const bytes = new Uint8Array(buffer, 0, magic.length);
    return magic.every((b, i) => bytes[i] === b);
};

export interface DocxViewerProps {
    /** Presigned/plain-fetchable URL of the .docx file (Data Room path). */
    fileUrl?: string;
    /** Already-fetched blob (Ask AICA's auth-gated path). Takes priority over fileUrl. */
    blob?: Blob;
}

/**
 * Read-only docx preview. Converts the file through the same docx→Plate
 * pipeline the Reports import uses (docxBlobToPlateValue), then serializes it
 * to HTML via plateToHtml — the same static serializer used for comment/email
 * previews — instead of mounting the full interactive PlateEditor (toolbar,
 * AI menu): this is a passive preview, and DocumentViewer has 9 consumers
 * that shouldn't all pay for the editor's toolbar bundle just to hit this
 * one branch. `.content` (PlateEditor.module.scss) is reused so the preview's
 * typography/table/list styling matches the live editor.
 */
const DocxViewer: React.FC<DocxViewerProps> = ({ fileUrl, blob }) => {
    const [html, setHtml] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        setHtml(null);

        const load = async () => {
            try {
                let source: Blob;
                if (blob) {
                    source = blob;
                } else if (fileUrl) {
                    const response = await fetch(fileUrl, { signal: controller.signal });
                    if (!response.ok) throw new Error('Failed to fetch file');
                    source = await response.blob();
                } else {
                    throw new Error('No document source provided');
                }

                const buffer = await source.arrayBuffer();
                if (hasMagic(buffer, OLE2_MAGIC)) {
                    toast.error("Legacy .doc isn't supported — please re-save it as .docx and try again.");
                    throw new Error('Legacy .doc is not supported');
                }
                if (!hasMagic(buffer, ZIP_MAGIC)) {
                    throw new Error('Unrecognised document format');
                }

                const value = await docxBlobToPlateValue(source);
                const rendered = await plateToHtml(value);
                if (isMounted) {
                    setHtml(rendered);
                    setLoading(false);
                }
            } catch (err) {
                if ((err as DOMException)?.name === 'AbortError') return;
                console.error('[DocxViewer] failed to load document', err instanceof Error ? err.message : 'unknown error');
                if (isMounted) {
                    setError(err instanceof Error ? err.message : 'Failed to load document');
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [fileUrl, blob]);

    if (loading) {
        return (
            <div className={styles.loadingBox}>
                <LoadingSpinner height="5rem" color="var(--primary-text-colour)" />
            </div>
        );
    }

    if (error || html == null) {
        return (
            <div className={styles.errorBox}>
                <p className={styles.errorText}>Unable to preview this document</p>
            </div>
        );
    }

    return (
        <div className={styles.docxContainer}>
            <div className={plateStyles.content} dangerouslySetInnerHTML={{ __html: sanitizeUntrustedHtml(html) }} />
        </div>
    );
};

export default DocxViewer;
